import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TreasuryClient } from "../lib/client/treasury-client.js";
import { getActiveClient, useBackendConfig } from "../lib/client";
import { Page } from "../Components/page.js";
import { ButtonOk } from "../Components/Buttons.js";
import { useProfiles } from "../lib/profiles";


function formatDate(date) {
    const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    };
    
    let d = new Date(date * 1000);
    // Add ordinal suffix (st, nd, rd, th)
    const day = d.getDate();
    const ordinal = getOrdinalSuffix(day);
    
    // Format the date part separately to insert ordinal
    const datePart = d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }).replace(/\s\d+\s/, ` ${day}${ordinal} `);
    
    const timePart = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    });
    
    return `${datePart} at ${timePart}`;
}

function getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
    }
}

function groupByStatus(items) {
    return items.reduce((acc, item) => {
        const status = item.status;
        if (!acc[status]) {
            acc[status] = [];
        }
        acc[status].push(item);
        return acc;
    }, {});
}

function statusClass(item) {



    /*dark:bg-",statusColor(item.status),"-900/30
    dark:text-",statusColor(item.status),"-400
    dark:ring-",statusColor(item.status),"-500/50*/
    
    let statuses = {
	"received":"bg-green-50 text-green-700 ring-green-600/20",
	"pending":"bg-yellow-50 text-yellow-700 ring-yellow-600/20",
	"paid":"bg-blue-50 text-blue-700 ring-blue-600/20",
	"error":"bg-red-50 text-red-700 ring-red-600/20",
	"invalid":"bg-red-50 text-red-700 ring-red-600/20"
    };
    return statuses[item.status] || "bg-gray-50 text-gray-700 ring-gray-600/20";
}

// todo pending items should be at the top
function SalesList({ items, precision }) {

    for (let item of items) {
	if (item.status == 'pending' && (Date.now()/1000 - item.createdAt) > 300) { //mark stuck pending as cancelled after 5 min
	    item.status = 'cancelled';
	}
    }
    
    return <div className="-mx-4 flow-root sm:mx-0 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 overflow-x-auto">
        <table className="min-w-full inline-block">
               <thead className="border-b border-gray-300 text-gray-900 dark:border-white/15 dark:text-white">
        <tr>
        <th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white">Transaction ID</th>
        <th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white">Request time</th>
        <th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white">Status</th>
        <th scope="col" class="py-3.5 pr-3 pl-4 text-right text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white">TDN sold</th>
        <th scope="col" class="py-3.5 pr-3 pl-4 text-right text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white">Expected {items[0]?.expectedPayoutUnits}</th>
               </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
                  <tr className="border-b border-gray-200 dark:border-white/10" key={item.id}>
                  <td className="py-2 pr-3 pl-4 text-sm whitespace-nowrap text-gray-500 sm:pl-0 dark:text-gray-400">{item.id.split('-')[4].toUpperCase()}</td>
                  <td className="px-3 py-5 text-left text-sm text-gray-500 sm:table-cell dark:text-gray-400">{formatDate(item.updatedAt)}</td>
                  <td className="px-3 py-5 text-left text-sm text-gray-500 sm:table-cell dark:text-gray-400">
                  <span class={["inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset uppercase", statusClass(item)].join(" ")}>{item.status}</span>
              </td>
                  <td className="px-3 py-5 text-right text-sm text-gray-500 sm:table-cell dark:text-gray-400">{item.sellValue.toFixed(precision || 3)}</td>
                  <td className="py-5 pr-4 pl-3 text-right text-sm text-gray-500 sm:pr-0 dark:text-gray-400">{item.expectedPayout.toFixed(2)}</td>
                  </tr>
          ))}
        </tbody>
        </table>
        </div>;
}

// show the user sell cost, conversion rate, and expected payout
// make the list nicer

export default function Sales() {
    const navigate = useNavigate();
    let treasuryConfig = useBackendConfig();

    let twinClient = getActiveClient();
    let { data: dq, dqLoading } = useQuery({
      queryKey: ["GET", `/dq/${treasuryConfig?.sell?.dq}`],
      queryFn: function getBalanceFn() {
        return twinClient.balance(treasuryConfig.sell.dq);
      },
      enabled: !!treasuryConfig
    });

    let treasuryClient = new TreasuryClient(getActiveClient())
    let { data: saleRequests } = useQuery({
      queryKey: ["GET", treasuryClient.host, "/sell"],
      queryFn: function getSalesFn(_) {
        return treasuryClient.getSellRequest();
      }
    });

    let { active } = useProfiles();
    let { data: treasuryProfile, isLoading } = useQuery({
        queryKey: ["GET", `/profile/${active}`],
        queryFn: async function getProfileFn() {
            return treasuryClient.getProfile()
        },
    });

    /*            <div className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3" role="alert">
                  <p className="font-bold">TDN Cashout Process</p>
                  <p className="text-sm">As an account holder, you not only have the ability to redeem your balance for a large library of creative content and services, but may also request to have funds deposited to your bank account.</p><br/>
                  <p className="text-sm">To initiate the cashout process:</p>
                  <ul className="list-disc list-inside mt-2 text-sm">
                  <li>Ensure you have met the minimum holdings of ${new Number(treasuryConfig.sell.dq_sell_min).toFixed(3)} USD.</li>
                  <li>Complete the one-time Banking Information setup questionnaire.</li>
                  </ul></div> */




    /*
               <div class="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
           <div class="mx-auto flex max-w-2xl items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
             <SalesList items={saleRequests || []} precision={dq.displayPrecision} />
            </div>
            </div>

             <div class="lg:col-start-3 lg:row-end-1">

         <div class="rounded-lg bg-gray-50 shadow-xs outline-1 outline-gray-900/5 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10">
         <dl class="flex flex-wrap">
         <div class="flex-auto pt-6 pl-6">
         <dt class="text-sm/6 font-semibold text-gray-900 dark:text-white">Amount</dt>
         <dd class="mt-1 text-base font-semibold text-gray-900 dark:text-white">$10,560.00</dd>
         </div></dl></div>
             </div>


*/
	    

    if (treasuryConfig) {
        return <Page>


      <div class="grid grid-cols-1 bg-gray-50 sm:grid-cols-2 lg:grid-cols-3 dark:bg-gray-700/10">
        <div class="border-t border-gray-200/50 px-4 py-6 sm:px-6 lg:px-8 dark:border-white/5">
          <p class="text-sm/6 font-medium text-gray-500 dark:text-gray-400">Current balance</p>
          <p class="mt-2 flex items-baseline gap-x-2">
            <span class="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">${dq && dq.balance > 0 ? new Number(dq.balance).toFixed(dq.displayPrecision) : "0.000"}</span>
            <span class="text-sm text-gray-500 dark:text-gray-400">USD-TDN</span>
          </p>
        </div>
        <div class="border-t border-gray-200/50 px-4 py-6 sm:border-l sm:px-6 lg:px-8 dark:border-white/5">
          <p class="text-sm/6 font-medium text-gray-500 dark:text-gray-400">Cumulative transferred to bank</p>
          <p class="mt-2 flex items-baseline gap-x-2">
            <span class="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">${saleRequests?.filter((x) => x.status == "paid").reduce((ps, x) => ps + x.expectedPayout, 0).toFixed(2) || "0.00"}</span>
            <span class="text-sm text-gray-500 dark:text-gray-400">USD</span>
          </p>
        </div>
        <div class="border-t border-gray-200/50 px-4 py-6 sm:px-6 lg:border-l lg:px-8 dark:border-white/5">
          <p class="text-sm/6 font-medium text-gray-500 dark:text-gray-400">Open withdrawal requests</p>
          <p class="mt-2 flex items-baseline gap-x-2">
            <span class="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">{saleRequests?.filter((x) => x.status == "pending" || x.status == "received").length || 0}</span>
          </p>
        </div>
      </div>

        
            <br/>
        {dq &&
         <div className="relative isolate pt-16">

<div class="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
  <div class="mx-auto grid max-w-2xl grid-cols-1 items-start gap-x-8 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
	 <div class="lg:col-span-2">
	 { saleRequests && saleRequests.length > 0 &&
	   <SalesList items={saleRequests || []} precision={dq.displayPrecision} />
	 }
	 { !saleRequests || saleRequests.length == 0 &&
	   <div class="font-medium text-gray-500 dark:text-gray-400 mt-10 text-center">No withdrawals have been created yet.</div>
	 }

	 </div>
    <div class="lg:col-start-3 lg:row-start-1">
      <div class="rounded-lg bg-gray-50 shadow-xs outline-1 outline-gray-900/5 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10">

{dq.balance < treasuryConfig.sell.dq_sell_min
             &&
             <div class="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4" role="alert">
             <p class="font-bold">Minimum Balance not Met</p>
 <p>You need to earn at least ${treasuryConfig.sell.dq_sell_min.toFixed(3)} USD-TDN to transfer funds to your bank.</p>
             </div>
            }

         <br />
         {!isLoading && !treasuryProfile &&
             <div className="flex items-center bg-blue-500 text-white text-sm font-bold px-4 py-3" role="alert">
             <svg className="fill-current w-4 h-4 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M12.432 0c1.34 0 2.01.912 2.01 1.957 0 1.305-1.164 2.512-2.679 2.512-1.269 0-2.009-.75-1.974-1.99C9.789 1.436 10.67 0 12.432 0zM8.309 20c-1.058 0-1.833-.652-1.093-3.524l1.214-5.092c.211-.814.246-1.141 0-1.141-.317 0-1.689.562-2.502 1.117l-.528-.88c2.572-2.186 5.531-3.467 6.801-3.467 1.057 0 1.233 1.273.705 3.23l-1.391 5.352c-.246.945-.141 1.271.106 1.271.317 0 1.357-.392 2.379-1.207l.6.814C12.098 19.02 9.365 20 8.309 20z"/></svg>
             <div className="">
             <p>Banking Profile</p>
             <p className="text-sm font-normal mt-1">In order to proceed with a withdrawal to your bank, you must first complete a banking profile.</p>
             <ButtonOk className="mt-2"
             onClick={() => navigate("/banking-info")}>
             Setup Banking Profile
             </ButtonOk>
             </div>
             </div>
             }
	     

	 {treasuryProfile && dq.balance >= treasuryConfig.sell.dq_sell_min && <div class="bg-blue-100 borter-t border-b text-blue-700 border-blue-700 px-4 py-3">
		 <p class="font-bold">Initiate Withdrawal</p>
		 <p class="text-sm">Request to transfer funds to your bank account by ACH transfer <span class="font-bold">(2-5 business days</span>)</p>
            <ButtonOk className="mt-2" hidden={dq.balance < treasuryConfig.sell.dq_sell_min}
                      onClick={() => navigate("/redeem")}>
                 Withdraw Funds to Bank
                 </ButtonOk></div>}

	 {treasuryProfile &&
         <div class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 mt-5" role="alert">
         <p class="font-bold">Keep your banking profile up-to-date</p>
         <p class="text-sm">Ensure funds are transferred to your intended account by checking your direct deposit settings before making a cashout request.</p>
         <ButtonOk className="mt-2 border-none bg-blue-500 shadow-none enabled:hover:bg-blue-300"
         onClick={() => navigate("/banking-info")}>
         Review Banking Profile
         </ButtonOk>
         </div>
        }

      </div>
    </div>
  </div>
</div>

	 


        </div>
        }
        </Page>;
    } else {
      return <Page>
        <div>User has no treasury config!</div>
      </Page>
    }
}
